import { useEffect, useMemo, useState } from "react";
import ProductItem from "./ProductItem";
import type { FlatProduct } from "../types";
import type { MonitorColorBy } from "../../../api/monitors";
import { rowColorFor } from "./rowColors";
import { useIsDarkTheme } from "../../../store/hooks";

interface SelectedProduct {
  itemId: number;
  billId: number;
  billElementId: number;
  productKey: string;
}

interface MonitorsGridViewProps {
  flatProducts: FlatProduct[];
  selectedProducts: SelectedProduct[];
  aggregatedTotals: Record<number, number>;
  formatTimeFromISO: (isoString: string) => string;
  calculateElapsedTime: (orderTime: string) => string;
  onProductClick: (product: FlatProduct) => void;
  onOrderHeaderClick: (orderKey: string) => void;
  // Клик по фона (извън картите) — сваля селекцията.
  onBackgroundClick: () => void;
  // По какво се оцветяват редовете (настройка на монитора).
  colorBy: MonitorColorBy;
  // Кои статуси значат „изпълнено" на този екран ("2" за обикновен монитор,
  // "3" за шпикер) — за проверката „цялата поръчка е приключена".
  fulfilledStatuses: string[];
  // Шпикер с „всички статуси": показва иконка и за незапочнато ("0"),
  // за да се различава от „в процес"/„сготвено" (в решетката "0" иначе е без).
  showUnstartedIcon: boolean;
}

const MonitorsGridView = ({
  flatProducts,
  selectedProducts,
  aggregatedTotals,
  formatTimeFromISO,
  calculateElapsedTime,
  onProductClick,
  onOrderHeaderClick,
  onBackgroundClick,
  colorBy,
  fulfilledStatuses,
  showUnstartedIcon,
}: MonitorsGridViewProps) => {
  // Пастелите са реални hex стойности (enhanceColor смята върху тях) →
  // тъмният набор се избира тук, не през CSS променлива.
  const isDark = useIsDarkTheme();
  // Динамична височина на прозореца (включително fullscreen)
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Разпределяне на продуктите в колони базирано на височина.
  // ВАЖНО: константите тук трябва да са 1:1 с реалните размери в
  // ProductItem.tsx (height 56, модификатор-ред 26 + 8 долен падинг,
  // хедър 56) + рамката и разстоянието на картата на поръчката
  // (ORDER_CARD_EXTRA: 2px border + 8px gap между картите), иначе
  // колоните преливат/зеят.
  const productsInColumns = useMemo(() => {
    const PRODUCT_HEIGHT = 56;
    const HEADER_HEIGHT = 56;
    // Всяка поръчка е отделна карта: 2px рамка (горна+долна) + 8px
    // разстояние до предишната карта (без разстояние за първата в колоната).
    const ORDER_CARD_BORDERS = 2;
    const ORDER_CARD_GAP = 8;
    const availableHeight = windowHeight - 200;

    const columns: FlatProduct[][] = [[]];
    let currentColumnIndex = 0;
    let currentColumnHeight = 0;

    flatProducts.forEach((product) => {
      const modifiersCount = product.modifiers?.length || 0;
      const productHeight =
        modifiersCount > 0
          ? PRODUCT_HEIGHT + modifiersCount * 26 + 8
          : PRODUCT_HEIGHT;

      const heightWithHeader = (cardGap: number) =>
        product.isFirstInOrder
          ? HEADER_HEIGHT + ORDER_CARD_BORDERS + cardGap + productHeight
          : productHeight;

      let itemHeight = heightWithHeader(
        currentColumnHeight === 0 ? 0 : ORDER_CARD_GAP,
      );

      if (
        currentColumnHeight + itemHeight > availableHeight &&
        currentColumnHeight > 0
      ) {
        currentColumnIndex++;
        columns[currentColumnIndex] = [];
        currentColumnHeight = 0;
        // Елементът става първи в новата колона → картата е без разстояние
        itemHeight = heightWithHeader(0);
      }

      columns[currentColumnIndex].push(product);
      currentColumnHeight += itemHeight;
    });

    return columns;
  }, [flatProducts, windowHeight]);

  return (
    <div
      className="flex h-full items-start gap-[10px] overflow-x-auto overflow-y-hidden pb-[4px]"
      // Кликовете по редове/хедъри спират bubbling-а → дотук стига само фонът
      onClick={onBackgroundClick}
    >
      {productsInColumns.map((columnProducts, columnIndex) => {
        // Групираме поредните продукти на една поръчка → една карта на поръчка.
        // Поръчка, продължила от предишната колона, е отделна карта без хедър.
        const orderCards: FlatProduct[][] = [];
        columnProducts.forEach((product) => {
          const lastCard = orderCards[orderCards.length - 1];
          if (
            lastCard &&
            lastCard[0].orderKey === product.orderKey &&
            !product.isFirstInOrder
          ) {
            lastCard.push(product);
          } else {
            orderCards.push([product]);
          }
        });
        return (
        <div
          key={columnIndex}
          // По-широка колона (340px) заради по-едрите шрифтове (17px име + 16.5px бр.)
          className="flex w-[340px] shrink-0 flex-col gap-[8px]"
        >
          {orderCards.map((cardProducts) => (
            <div
              key={`${cardProducts[0].orderKey}-${cardProducts[0].productIndex}`}
              className="overflow-hidden rounded-[12px] border border-shade-3 bg-surface shadow-[0_2px_6px_rgba(17,17,17,0.10)]"
            >
              {cardProducts.map((product) => {
            const productKey = `${product.orderKey}-${product.productIndex}`;
            const rowColor = rowColorFor(
              colorBy,
              product.tableName,
              product.waiterName,
              isDark,
            );
            const isSelected = selectedProducts.some(
              (p) => p.productKey === productKey,
            );

            // Cancelled продукти се считат за завършени при тази проверка
            const orderProducts = flatProducts.filter(
              (p) => p.orderKey === product.orderKey,
            );
            const isOrderCompleted = orderProducts.every(
              (p) => p.isCancelled || fulfilledStatuses.includes(p.status),
            );

            const elapsedTime = product.isFirstInOrder
              ? calculateElapsedTime(product.time)
              : undefined;

            const currentIndexInFlatList = flatProducts.findIndex(
              (p) => `${p.orderKey}-${p.productIndex}` === productKey,
            );
            const prevProductInFlatList =
              currentIndexInFlatList > 0
                ? flatProducts[currentIndexInFlatList - 1]
                : null;
            const nextProductInFlatList =
              currentIndexInFlatList < flatProducts.length - 1
                ? flatProducts[currentIndexInFlatList + 1]
                : null;

            // Цялата верига от добавки получава усилен фон, не само първата
            const shouldEnhanceBackground =
              (product.hasAdditions &&
                nextProductInFlatList?.isAddition &&
                nextProductInFlatList?.orderKey === product.orderKey) ||
              (product.isAddition &&
                (prevProductInFlatList?.hasAdditions ||
                  prevProductInFlatList?.isAddition) &&
                prevProductInFlatList?.orderKey === product.orderKey);

            return (
              <ProductItem
                key={productKey}
                productName={product.productName}
                isLunch={product.isLunch}
                lunchUnitAndSize={product.lunchUnitAndSize}
                quantity={product.quantity}
                completedQuantity={product.completedQuantity}
                status={product.status}
                isSplit={product.isSplit}
                isCancelled={product.isCancelled}
                showOrderHeader={product.isFirstInOrder}
                orderTableName={product.tableName}
                orderOldTableName={product.oldTableName}
                orderWaiterName={product.waiterName}
                orderTime={formatTimeFromISO(product.time)}
                orderComment={product.comment}
                elapsedTime={elapsedTime}
                isOrderCompleted={isOrderCompleted}
                backgroundColor={rowColor}
                isSelected={isSelected}
                onClick={() => onProductClick(product)}
                onHeaderClick={() => onOrderHeaderClick(product.orderKey)}
                modifiers={product.modifiers}
                hasAdditions={product.hasAdditions}
                isAddition={product.isAddition}
                mainProductName={product.mainProductName}
                mainStationName={product.mainStationName}
                stationTag={product.stationTag}
                stationHint={product.stationHint}
                shouldEnhanceBackground={shouldEnhanceBackground}
                aggregatedTotal={aggregatedTotals[product.itemId]}
                isOverdue={product.isOverdue}
                fulfilledStatuses={fulfilledStatuses}
                showUnstartedIcon={showUnstartedIcon}
              />
                );
              })}
            </div>
          ))}
        </div>
        );
      })}
    </div>
  );
};

export default MonitorsGridView;
